import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAPI } from '../../API';
import { useAppDispatch, useAppState } from '../../Store';
import { useTranslation } from '../../Translation';
import SidebarNav from '../../Components/SidebarNav/SidebarNav';
import SettingSidebarNav from '../../Components/SidebarNav/SettingSidebarNav';
import useAuthenticated from '../Dashboard/useAuthenticated';
import GridTable from '../../Components/Table/GridTable';
import { usePaginations } from '../../Hooks/usePaginations';
import { TextInput } from '../../Components/Inputs/TextInput';
import { Button } from '../../Components/Buttons';
import { useRefresh } from '../../Hooks/useRefresh';
import Modal from '../../Components/Modal';
import { TrashIcon } from '@heroicons/react/24/outline';
import { decompressData } from '../../Utils';
import { ActionTypes } from '../../Store/Stores/User';
import { CustomField } from '../Contacts/Contact';

export default function GlobalVariable() {


    const { translate, language, setLanguage } = useTranslation();
    const api = useAPI();
    const dispatch = useAppDispatch();
    const appState = useAppState();
    const user = appState.user;
    const [open, setOpen] = useState(false);
    const [globalVariables, setGlobalVariables] = useState([]);
    const { refreshId, refresh } = useRefresh();

    const { isAuthenticated, pathAfterLogin } = useAuthenticated();

    useEffect(() => {
        const fetchUser = async () => {
            const userResponse = await api.getUser();
            dispatch({
                type: ActionTypes.SET_USER,
                payload: userResponse.data.user
            });
        }

        fetchUser();
    }, [refreshId]);

    useEffect(() => {
        if (!user || !user.data) return;
        setGlobalVariables(decompressData(user.data));
    }, [user]);

    const setGlobalVariableOpen = useCallback((field, open) => {
        field.customFieldOpen = open;
        setGlobalVariables([...globalVariables]);
    }, [globalVariables]);

    return (
        <>
         {/* Place the Modal outside the main container */}
         <Modal
                open={open}
                onClose={() => setOpen(false)}
                title={translate('setting.globalVariable.title')}
                description={translate('setting.globalVariable.add_description')}
                children={<GlobalVariableModal refresh={refresh} setOpen={setOpen} open={open} user={user} />}
            />
        <div className="flex min-h-full w-full">
            <SidebarNav currentPage="settings" contentPadding={false}>
                <div className='flex-1 flex-row h-full w-full'>
                    <SettingSidebarNav currentPage="globalVariable" />

                        <div className='flex-1 flex flex-col ml-24 md:ml-72'>
                            <div className='flex flex-col p-2 md:p-4 lg:p-6 w-full'>
                                <div className='flex flex-row justify-between'>
                                    <div>
                                        <h3 className='text-2xl font-bold'>{translate('setting.globalVariable.title')}</h3>
                                        <p className='text-md text-gray-500 max-w-4xl'>{translate('setting.globalVariable.description')}</p>
                                    </div>
                                    <Button onClick={() => setOpen(true)}>{translate('setting.globalVariable.add')}</Button>
                                </div>


                                <div className="flex flex-col mt-8 border-r border-gray-200 pr-8">
                                    <div className="mt-6 border-t border-gray-100">
                                        <dl className="divide-y divide-gray-100 ml-4">
                                            {globalVariables.map((field) => (<div key={field.name + field.value}><CustomField setCustomFieldOpen={setGlobalVariableOpen} field={field} /></div>))}
                                        </dl>
                                    </div>
                                </div>
                            </div>


                        </div>
                </div>
                
            </SidebarNav>
        </div>
        </>
    );
}

export const GlobalVariableModal = ({ refresh, setOpen, open, user }) => {

    const [variableName, setVariableName] = useState('');
    const [variableValue, setVariableValue] = useState('');
    const { translate } = useTranslation();
    const api = useAPI();

    const handleSave = async () => {
        try {

            // Try to parse as number, but keep original value if parsing fails
            const parsedValue = Number(variableValue);
            const finalValue = isNaN(parsedValue) ? variableValue : parsedValue;

            await api.updateUser({
                ...user,
                data: {
                    [variableName]: finalValue
                }
            });

            refresh();

            setOpen(false);
            setVariableName('');
            setVariableValue('');
        } catch (error) {
            console.error(error);
        }
    }

  
    useEffect(() => {
        if (!open) {
            setVariableName('');
            setVariableValue('');
        }
    }, [open]);

    const buttonDisabled = !variableName || variableName.length === 0 || !variableValue || variableValue.length === 0;

    return (
        <div className="mt-4 overflow-visible">
            <TextInput id="variable_name" name="variable_name" label={translate('setting.globalVariable.variable_name')} placeholder={translate('setting.globalVariable.variable_name_placeholder')} value={variableName} onChange={(e) => setVariableName(e.target.value)} />
            <div className="flex flex-col mt-4">
                <TextInput id="variable_value" name="variable_value" label={translate('setting.globalVariable.variable_value')} placeholder={translate('setting.globalVariable.variable_value_placeholder')} value={variableValue} onChange={(e) => setVariableValue(e.target.value)} />

            </div>
            
            <div className="flex justify-end mt-4">
                <Button disabled={buttonDisabled} onClick={handleSave}>{translate('save')}</Button>
            </div>
        </div>
    )
}
